import { useState, useEffect, useCallback, useRef } from "react"
import Box from "@mui/material/Box"
import Card from "@mui/material/Card"
import Stack from "@mui/material/Stack"
import Typography from "@mui/material/Typography"
import TextField from "@mui/material/TextField"
import MenuItem from "@mui/material/MenuItem"
import Button from "@mui/material/Button"
import Switch from "@mui/material/Switch"
import InputAdornment from "@mui/material/InputAdornment"
import { Iconify } from "@/src/common/iconify"
import { toast } from "@/src/common/snackbar"
import { DashboardContent } from "@/src/layouts/dashboard"
import { useInternalStore } from "@/src/store/slices/internal.slice"
import { patchWorkspaceCredits, type PatchWorkspaceCreditsPayload } from "@/src/services/internal"
import { CHARGING_TYPE_OPTIONS, type ChargingTypeValue } from "@/src/features/internalUsers/constants"
import {
    CreditsAndVASFormSchema,
    DEFAULT_CREDITS_AND_VAS_FORM_VALUES,
    type CreditsAndVASFormValues,
} from "@/src/features/internalUsers/schemas/CreditsAndVAS"
import type { InternalWorkspaceApi } from "@/src/store/slices/internal.slice"
import type { ZodError } from "zod"

function zodFieldErrors(error: ZodError): Record<string, string> {
    const out: Record<string, string> = {}
    for (const e of error.errors) {
        const key = e.path.join(".")
        if (key) out[key] = e.message
    }
    return out
}

// ----------------------------------------------------------------------

function getAddonNumber(addons: Record<string, unknown> | undefined, key: string, fallback: number): number {
    const v = addons?.[key]
    if (typeof v === "number" && !Number.isNaN(v)) return v
    const n = Number(v)
    return Number.isNaN(n) ? fallback : n
}

function getAddonBool(addons: Record<string, unknown> | undefined, key: string, fallback: boolean): boolean {
    const v = addons?.[key]
    if (typeof v === "boolean") return v
    return fallback
}

/** Builds a PATCH body from current form vs last synced snapshot (only changed sections). */
function buildCreditsPatch(
    values: CreditsAndVASFormValues,
    snap: CreditsAndVASFormValues,
    adds: { addVideo: number; addAudio: number; addOutbound: number }
): PatchWorkspaceCreditsPayload {
    const patch: PatchWorkspaceCreditsPayload = {}
    const nextVideo = values.videoCreditsAvailable + adds.addVideo
    const nextAudio = values.audioCreditsAvailable + adds.addAudio
    const nextOutbound = values.outboundAudioCreditsAvailable + adds.addOutbound
    const maxAttempts = parseInt(values.attemptFrequency, 10) || 3
    const snapMax = parseInt(snap.attemptFrequency, 10) || 3

    const videoChanged =
        values.videoEnabled !== snap.videoEnabled ||
        nextVideo !== snap.videoCreditsAvailable ||
        values.videoPulseRate !== snap.videoPulseRate ||
        values.videoCefrEnabled !== snap.videoCefrEnabled ||
        values.videoProctoredMode !== snap.videoProctoredMode
    if (videoChanged) {
        patch.videoCredits = {
            isEnabled: values.videoEnabled,
            totalCredits: nextVideo,
            addons: {
                pulse: parseInt(values.videoPulseRate, 10) || 3,
                proctoring: values.videoProctoredMode,
                cefr: values.videoCefrEnabled,
            },
        }
    }

    const audioChanged =
        values.audioEnabled !== snap.audioEnabled ||
        nextAudio !== snap.audioCreditsAvailable ||
        values.audioPulseRate !== snap.audioPulseRate ||
        values.audioCefrEnabled !== snap.audioCefrEnabled
    if (audioChanged) {
        patch.audioCredits = {
            isEnabled: values.audioEnabled,
            totalCredits: nextAudio,
            addons: {
                pulse: parseInt(values.audioPulseRate, 10) || 3,
                proctoring: false,
                cefr: values.audioCefrEnabled,
            },
        }
    }

    const outboundChanged =
        values.outboundAudioEnabled !== snap.outboundAudioEnabled ||
        nextOutbound !== snap.outboundAudioCreditsAvailable ||
        values.outboundAudioPulseRate !== snap.outboundAudioPulseRate ||
        values.outboundAudioCefrEnabled !== snap.outboundAudioCefrEnabled
    if (outboundChanged) {
        patch.outboundAudioCredits = {
            isEnabled: values.outboundAudioEnabled,
            totalCredits: nextOutbound,
            addons: {
                pulse: parseInt(values.outboundAudioPulseRate, 10) || 3,
                proctoring: false,
                cefr: values.outboundAudioCefrEnabled,
            },
        }
    }

    if (values.chargingType !== snap.chargingType || maxAttempts !== snapMax) {
        patch.chargeAttemptRule = values.chargingType
        patch.maxAttempts = maxAttempts
    }

    return patch
}

/** When API omits `outboundAudioCredits`, derive from root flag + audio (matches workspace toggles fallback). */
function resolveOutboundAudioCredits(ws: InternalWorkspaceApi) {
    const ac = ws.audioCredits
    if (ws.outboundAudioCredits) return ws.outboundAudioCredits
    if (typeof ws.outboundAudioEnabled === "boolean") {
        return {
            isEnabled: ws.outboundAudioEnabled,
            totalCredits: ac?.totalCredits ?? 0,
            addons: ac?.addons,
        }
    }
    return { isEnabled: false, totalCredits: 0, addons: undefined as Record<string, unknown> | undefined }
}

const ADD_CREDIT_FIELDS = [
    { label: "Video Credits", fieldKey: "addVideoCredits" as const, enabledKey: "videoEnabled" as const },
    { label: "Audio Credits", fieldKey: "addAudioCredits" as const, enabledKey: "audioEnabled" as const },
    {
        label: "Outbound Audio Credits",
        fieldKey: "addOutboundAudioCredits" as const,
        enabledKey: "outboundAudioEnabled" as const,
    },
] as const

const creditFeatureRowSx = {
    p: 1.5,
    borderRadius: 1,
    bgcolor: "grey.50",
    border: 1,
    borderColor: "divider",
} as const

const availableCreditsStatBoxSx = {
    flex: 1,
    p: 2,
    borderRadius: 2,
    bgcolor: "grey.50",
    border: 1,
    borderColor: "divider",
    textAlign: "center",
} as const

function AvailableCreditsStat(props: { label: string; value: number }) {
    const { label, value } = props
    return (
        <Box sx={availableCreditsStatBoxSx}>
            <Typography variant="overline" sx={{ color: "text.secondary", fontWeight: 600 }}>
                {label}
            </Typography>
            <Typography variant="h3" fontWeight={700} color="primary.main" sx={{ mt: 0.5 }}>
                {value.toLocaleString()}
            </Typography>
        </Box>
    )
}

function AddCreditsNumberField(props: {
    label: string
    value: string
    disabled: boolean
    onValueChange: (v: string) => void
    error?: string
}) {
    const { label, value, disabled, onValueChange, error } = props
    return (
        <TextField
            fullWidth
            label={label}
            type="number"
            value={value}
            onChange={(e) => onValueChange(e.target.value)}
            size="small"
            disabled={disabled}
            inputProps={{ min: 0 }}
            error={Boolean(error)}
            helperText={error}
        />
    )
}

function CreditChannelCard(props: {
    title: string
    disabledMessage: string
    enabled: boolean
    onEnabledChange: (v: boolean) => void
    creditsAvailable: number
    pulseRate: string
    onPulseRateChange: (v: string) => void
    pulseFieldError?: string
    cefrEnabled: boolean
    onCefrChange: (v: boolean) => void
    cefrDescription: string
    /** When set, renders Proctored Mode row (video only). */
    proctoringEnabled?: boolean
    onProctoringChange?: (v: boolean) => void
}) {
    const {
        title,
        disabledMessage,
        enabled,
        onEnabledChange,
        creditsAvailable,
        pulseRate,
        onPulseRateChange,
        pulseFieldError,
        cefrEnabled,
        onCefrChange,
        cefrDescription,
        proctoringEnabled,
        onProctoringChange,
    } = props
    const showProctoring = onProctoringChange !== undefined
    return (
        <Card sx={{ p: 2.5, borderRadius: 2 }}>
            <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: enabled ? 2 : 0 }}>
                <Typography variant="subtitle2">{title}</Typography>
                <Switch checked={enabled} onChange={(e) => onEnabledChange(e.target.checked)} color="primary" />
            </Stack>
            {!enabled ? (
                <Typography variant="body2" color="text.secondary" sx={{ fontStyle: "italic" }}>
                    {disabledMessage}
                </Typography>
            ) : (
                <Stack spacing={2.5}>
                    <Typography variant="overline" sx={{ color: "text.secondary", fontWeight: 600 }}>
                        Limits & Pulse Rate
                    </Typography>
                    <Stack direction={{ xs: "column", sm: "row" }} spacing={2} flexWrap="wrap">
                        <TextField
                            label="Total Credits"
                            type="number"
                            value={creditsAvailable}
                            disabled
                            size="small"
                            sx={{ minWidth: 120 }}
                            inputProps={{ min: 0 }}
                        />
                        <TextField
                            label="Pulse Rate (mins)"
                            type="number"
                            value={pulseRate}
                            onChange={(e) => onPulseRateChange(e.target.value)}
                            size="small"
                            sx={{ minWidth: 140 }}
                            inputProps={{ min: 0 }}
                            error={Boolean(pulseFieldError)}
                            helperText={pulseFieldError}
                        />
                    </Stack>
                    <Typography
                        variant="overline"
                        sx={{ color: "text.secondary", fontWeight: 600, display: "block", pt: 1 }}
                    >
                        Feature Toggles
                    </Typography>
                    <Stack spacing={1.5}>
                        <Stack
                            direction="row"
                            alignItems="center"
                            justifyContent="space-between"
                            sx={creditFeatureRowSx}
                        >
                            <Box>
                                <Typography variant="subtitle2">CEFR Enabled</Typography>
                                <Typography variant="caption" color="text.secondary">
                                    {cefrDescription}
                                </Typography>
                            </Box>
                            <Switch
                                checked={cefrEnabled}
                                onChange={(e) => onCefrChange(e.target.checked)}
                                color="primary"
                            />
                        </Stack>
                        {showProctoring && (
                            <Stack
                                direction="row"
                                alignItems="center"
                                justifyContent="space-between"
                                sx={creditFeatureRowSx}
                            >
                                <Box>
                                    <Typography variant="subtitle2">Proctored Mode</Typography>
                                    <Typography variant="caption" color="text.secondary">
                                        Enable anti-cheating measures during video interviews.
                                    </Typography>
                                </Box>
                                <Switch
                                    checked={proctoringEnabled}
                                    onChange={(e) => onProctoringChange?.(e.target.checked)}
                                    color="primary"
                                />
                            </Stack>
                        )}
                    </Stack>
                </Stack>
            )}
        </Card>
    )
}

export default function CreditsAndVAS() {
    const { workspaces, workspacesDetail, fetchWorkspaces } = useInternalStore()
    const [selectedWorkspaceId, setSelectedWorkspaceId] = useState("")

    const selectedWorkspace = selectedWorkspaceId
        ? workspacesDetail.find((w) => w.workspaceId === selectedWorkspaceId)
        : null

    useEffect(() => {
        fetchWorkspaces({ page: 0, size: 100 })
    }, [fetchWorkspaces])

    useEffect(() => {
        if (workspaces.length > 0 && !selectedWorkspaceId) {
            setSelectedWorkspaceId(workspaces[0].id)
        }
    }, [workspaces, selectedWorkspaceId])

    const [form, setForm] = useState<CreditsAndVASFormValues>(DEFAULT_CREDITS_AND_VAS_FORM_VALUES)
    /** Form snapshot after last sync from API — used to build partial PATCH bodies. */
    const lastSyncedFormRef = useRef<CreditsAndVASFormValues | null>(null)
    const [saving, setSaving] = useState(false)
    const [saveError, setSaveError] = useState("")
    const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({})

    const setFormField = useCallback(
        <K extends keyof CreditsAndVASFormValues>(key: K, value: CreditsAndVASFormValues[K]) => {
            setForm((prev) => ({ ...prev, [key]: value }))
            setFieldErrors((prev) => {
                const next = { ...prev }
                delete next[key as string]
                return next
            })
        },
        []
    )

    // Sync form state from selected workspace API response
    useEffect(() => {
        const ws = selectedWorkspaceId ? workspacesDetail.find((w) => w.workspaceId === selectedWorkspaceId) : null
        if (!ws) return
        const vc = ws.videoCredits
        const ac = ws.audioCredits
        const oac = resolveOutboundAudioCredits(ws)
        const parsed = CreditsAndVASFormSchema.safeParse({
            ...DEFAULT_CREDITS_AND_VAS_FORM_VALUES,
            videoCreditsAvailable: vc?.totalCredits ?? 0,
            audioCreditsAvailable: ac?.totalCredits ?? 0,
            outboundAudioCreditsAvailable: oac.totalCredits ?? 0,
            videoPulseRate: String(getAddonNumber(vc?.addons, "pulse", 3)),
            audioPulseRate: String(getAddonNumber(ac?.addons, "pulse", 3)),
            outboundAudioPulseRate: String(
                getAddonNumber(oac.addons as Record<string, unknown> | undefined, "pulse", 3)
            ),
            chargingType:
                (ws.chargeAttemptRule as ChargingTypeValue) ?? DEFAULT_CREDITS_AND_VAS_FORM_VALUES.chargingType,
            attemptFrequency: String(ws.maxAttempts ?? 3),
            videoEnabled: vc?.isEnabled ?? true,
            audioEnabled: ac?.isEnabled ?? true,
            outboundAudioEnabled: oac.isEnabled ?? false,
            videoCefrEnabled: getAddonBool(vc?.addons, "cefr", true),
            videoProctoredMode: getAddonBool(vc?.addons, "proctoring", false),
            audioCefrEnabled: getAddonBool(ac?.addons, "cefr", true),
            outboundAudioCefrEnabled: getAddonBool(oac.addons as Record<string, unknown> | undefined, "cefr", true),
        })
        if (parsed.success) {
            setForm(parsed.data)
            lastSyncedFormRef.current = parsed.data
        }
        setSaveError("")
        setFieldErrors({})
    }, [selectedWorkspaceId, workspacesDetail])

    const handleSaveAll = async () => {
        if (!selectedWorkspaceId || !selectedWorkspace) return
        const parsed = CreditsAndVASFormSchema.safeParse(form)
        if (!parsed.success) {
            setFieldErrors(zodFieldErrors(parsed.error))
            return
        }
        setFieldErrors({})
        const values = parsed.data
        setSaving(true)
        setSaveError("")
        try {
            const snap = lastSyncedFormRef.current
            if (!snap) {
                setSaveError("Workspace data is still loading. Please try again.")
                return
            }
            const addVideo = parseInt(values.addVideoCredits, 10) || 0
            const addAudio = parseInt(values.addAudioCredits, 10) || 0
            const addOutbound = parseInt(values.addOutboundAudioCredits, 10) || 0
            const patch = buildCreditsPatch(values, snap, { addVideo, addAudio, addOutbound })
            if (Object.keys(patch).length === 0) {
                toast.info("No changes to save.")
                return
            }
            await patchWorkspaceCredits(selectedWorkspaceId, patch)
            await fetchWorkspaces()
            setFormField("addVideoCredits", "0")
            setFormField("addAudioCredits", "0")
            setFormField("addOutboundAudioCredits", "0")
        } catch {
            setSaveError("Failed to save. Please try again.")
        } finally {
            setSaving(false)
        }
    }

    return (
        <DashboardContent sx={{ flexGrow: 1, display: "flex", flexDirection: "column", paddingBottom: "20px", mt: 5 }}>
            <Stack direction="row" justifyContent="space-between" alignItems="flex-start" sx={{ mb: 3 }}>
                <Box>
                    <Typography variant="h4" fontWeight={700}>
                        Credits & VAS
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                        Manage credits, pulse rates, charging, and feature toggles per workspace.
                    </Typography>
                </Box>
                <Button
                    variant="contained"
                    startIcon={<Iconify icon="mdi:content-save-outline" width={22} />}
                    onClick={handleSaveAll}
                    disabled={
                        saving ||
                        !selectedWorkspaceId ||
                        (!form.videoEnabled && !form.audioEnabled && !form.outboundAudioEnabled)
                    }
                >
                    {saving ? "Saving..." : "Save All"}
                </Button>
            </Stack>

            {saveError && (
                <Typography variant="body2" color="error" sx={{ mb: 1 }}>
                    {saveError}
                </Typography>
            )}

            <Stack spacing={3}>
                {/* Select Workspace */}
                <Card sx={{ p: 2.5, borderRadius: 2 }}>
                    <Typography variant="subtitle2" sx={{ mb: 1.5, display: "block" }}>
                        Select Workspace
                    </Typography>
                    <TextField
                        fullWidth
                        select
                        value={selectedWorkspaceId}
                        onChange={(e) => setSelectedWorkspaceId(e.target.value)}
                        size="small"
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start" sx={{ mr: 0.5 }}>
                                    <Iconify icon="mdi:office-building" width={22} sx={{ color: "text.secondary" }} />
                                </InputAdornment>
                            ),
                        }}
                    >
                        {workspaces.map((w) => (
                            <MenuItem key={w.id} value={w.id}>
                                {w.name}
                            </MenuItem>
                        ))}
                    </TextField>
                </Card>

                {/* Available Credits */}
                <Card sx={{ p: 2.5, borderRadius: 2 }}>
                    <Typography variant="subtitle2" sx={{ mb: 2, display: "block" }}>
                        Available Credits
                    </Typography>
                    <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
                        {(
                            [
                                ["Video Credits", form.videoCreditsAvailable],
                                ["Audio Credits", form.audioCreditsAvailable],
                                ["Outbound Audio Credits", form.outboundAudioCreditsAvailable],
                            ] as const
                        ).map(([label, value]) => (
                            <AvailableCreditsStat key={label} label={label} value={value} />
                        ))}
                    </Stack>
                    {selectedWorkspace &&
                        (() => {
                            const videoOff = !selectedWorkspace.videoCredits?.isEnabled
                            const audioOff = !selectedWorkspace.audioCredits?.isEnabled
                            const outboundOff = !resolveOutboundAudioCredits(selectedWorkspace).isEnabled
                            if (!videoOff && !audioOff && !outboundOff) return null
                            const labels: string[] = []
                            if (videoOff) labels.push("Video")
                            if (audioOff) labels.push("Audio")
                            if (outboundOff) labels.push("Outbound audio")
                            let msg: string
                            if (labels.length === 1) {
                                msg = `${labels[0]} has been disabled.`
                            } else if (labels.length === 2) {
                                msg = `${labels[0]} and ${labels[1]} have been disabled.`
                            } else {
                                msg = `${labels.slice(0, -1).join(", ")}, and ${labels[labels.length - 1]} have been disabled.`
                            }
                            return (
                                <Typography variant="body2" color="text.secondary" sx={{ mt: 2, fontStyle: "italic" }}>
                                    {msg}
                                </Typography>
                            )
                        })()}
                </Card>

                {/* Add Credits */}
                <Card sx={{ p: 2.5, borderRadius: 2 }}>
                    <Typography variant="subtitle2" sx={{ mb: 2, display: "block" }}>
                        Add Credits
                    </Typography>
                    <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
                        {ADD_CREDIT_FIELDS.map(({ label, fieldKey, enabledKey }) => (
                            <AddCreditsNumberField
                                key={fieldKey}
                                label={label}
                                value={form[fieldKey]}
                                disabled={!form[enabledKey]}
                                onValueChange={(v) => setFormField(fieldKey, v)}
                                error={fieldErrors[fieldKey]}
                            />
                        ))}
                    </Stack>
                </Card>

                {/* Attempts & Charging - outside Video/Audio toggles */}
                <Card sx={{ p: 2.5, borderRadius: 2 }}>
                    <Typography variant="subtitle2" sx={{ mb: 2, display: "block" }}>
                        Attempts & Charging
                    </Typography>
                    <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
                        <TextField
                            fullWidth
                            label="Attempt Frequency"
                            type="number"
                            value={form.attemptFrequency}
                            onChange={(e) => setFormField("attemptFrequency", e.target.value)}
                            size="small"
                            inputProps={{ min: 0 }}
                            error={Boolean(fieldErrors.attemptFrequency)}
                            helperText={fieldErrors.attemptFrequency}
                        />
                        <TextField
                            fullWidth
                            select
                            label="Charging Type"
                            value={form.chargingType}
                            onChange={(e) => setFormField("chargingType", e.target.value as ChargingTypeValue)}
                            size="small"
                        >
                            {CHARGING_TYPE_OPTIONS.map((opt) => (
                                <MenuItem key={opt.value} value={opt.value}>
                                    {opt.label}
                                </MenuItem>
                            ))}
                        </TextField>
                    </Stack>
                </Card>

                <CreditChannelCard
                    title="Video"
                    disabledMessage="Video has been disabled."
                    enabled={form.videoEnabled}
                    onEnabledChange={(v) => setFormField("videoEnabled", v)}
                    creditsAvailable={form.videoCreditsAvailable}
                    pulseRate={form.videoPulseRate}
                    onPulseRateChange={(v) => setFormField("videoPulseRate", v)}
                    pulseFieldError={fieldErrors.videoPulseRate}
                    cefrEnabled={form.videoCefrEnabled}
                    onCefrChange={(v) => setFormField("videoCefrEnabled", v)}
                    cefrDescription="Enable CEFR language evaluation for video interviews."
                    proctoringEnabled={form.videoProctoredMode}
                    onProctoringChange={(v) => setFormField("videoProctoredMode", v)}
                />

                <CreditChannelCard
                    title="Audio"
                    disabledMessage="Audio has been disabled."
                    enabled={form.audioEnabled}
                    onEnabledChange={(v) => setFormField("audioEnabled", v)}
                    creditsAvailable={form.audioCreditsAvailable}
                    pulseRate={form.audioPulseRate}
                    onPulseRateChange={(v) => setFormField("audioPulseRate", v)}
                    pulseFieldError={fieldErrors.audioPulseRate}
                    cefrEnabled={form.audioCefrEnabled}
                    onCefrChange={(v) => setFormField("audioCefrEnabled", v)}
                    cefrDescription="Enable CEFR language evaluation for audio interviews."
                />

                <CreditChannelCard
                    title="Outbound Audio"
                    disabledMessage="Outbound audio has been disabled."
                    enabled={form.outboundAudioEnabled}
                    onEnabledChange={(v) => setFormField("outboundAudioEnabled", v)}
                    creditsAvailable={form.outboundAudioCreditsAvailable}
                    pulseRate={form.outboundAudioPulseRate}
                    onPulseRateChange={(v) => setFormField("outboundAudioPulseRate", v)}
                    pulseFieldError={fieldErrors.outboundAudioPulseRate}
                    cefrEnabled={form.outboundAudioCefrEnabled}
                    onCefrChange={(v) => setFormField("outboundAudioCefrEnabled", v)}
                    cefrDescription="Enable CEFR language evaluation for outbound (phone) audio interviews."
                />
            </Stack>
        </DashboardContent>
    )
}
